import { Input } from '@rocketseat/unform';
import classnames from 'classnames';
import { StyledSvgSprite } from 'components/general/SVGSprite';
import { Box, FlexDiv } from 'components/molecules/utils/basicComponents';
import { Typography } from 'foundations/typography';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

/**
 * A custom field component for the form
 * Note - This is an uncontrolled component, So it needs to be wrapped
 * in a parent Form componet from @unform package which controls the state
 * of the Input Component
 * TODO: Add params description
 */
export default function FormField({
    id,
    label,
    type = 'text',
    name,
    placeholder = '',
    filledValue,
    className = '',
    onChange = () => {},
    autoFocus = false,
    maxLength,
    isError = false,
    errorMsg,
    errorTextVarient = 'caption',
    hideLimit = false,
    multiline,
    min,
    max,
    custom,
    children,
    elementType,
    inputValue = null,
    renderBottomText,
    onFocus = () => {},
    onBlur = () => {},
    onClick = () => {},
    variant = 'default',
    subLabel = '',
    isLightBg = false,
    hasButtons,
    onDecrement = () => {},
    onIncrement = () => {},
    newOnBoardingFlow = false,
    isForcedLight = false,
    labelColor = null,
    subLabelColor = null,
    labelVariant = 'subtext1',
    charLimitColor = null,
    charLimitVariant = 'caption',
    onKeyUp = () => {},
    onKeyPress = () => {},
    ...props
}) {
    const [val, setVal] = useState(filledValue);
    const renderCharLimit = (inputVal) => {
        return (
            <Typography
                variant={charLimitVariant}
                className='count'
                color={
                    charLimitColor ||
                    (variant ? (isLightBg ? 'ambience.14' : 'ambience.3') : '')
                }
            >
                {inputVal !== null ? inputVal?.length || 0 : val?.length || 0}/
                {maxLength}
            </Typography>
        );
    };
    const renderSizeLimit = () => {
        return (
            <Typography
                variant={'caption'}
                className='count'
                color={
                    variant ? (isLightBg ? 'ambience.14' : 'ambience.3') : ''
                }
            >
                {props.value || 0}/{max}
            </Typography>
        );
    };
    const onValueChange = (e) => {
        setVal(e.target.value);
        onChange(e);
    };

    const isFilled = Array.isArray(filledValue)
        ? filledValue.length > 0
        : filledValue;

    return (
        <>
            {variant === 'default' && (
                <>
                    <Box
                        className={classnames(
                            'form-field',
                            {
                                error: isError,
                                filled:
                                    !props.disabled &&
                                    (isFilled || typeof isFilled === 'number'),
                            },
                            className
                        )}
                    >
                        <Label htmlFor={id}>{label}</Label>
                        {!custom && (
                            <Input
                                type={type}
                                name={name}
                                id={id}
                                placeholder={placeholder}
                                maxLength={maxLength}
                                onChange={onValueChange}
                                multiline={multiline}
                                min={min}
                                max={max}
                                onFocus={onFocus}
                                onBlur={onBlur}
                                {...props}
                            />
                        )}
                        {custom && <>{children}</>}
                    </Box>
                    <FlexDiv
                        className={classnames({
                            'justify-content-end': !renderBottomText,
                            'justify-content-between': renderBottomText,
                        })}
                    >
                        {errorMsg && (
                            <Paragraph
                                className={classnames('error-msg', {
                                    invisible: !isError,
                                })}
                            >
                                {errorMsg}
                            </Paragraph>
                        )}
                        {renderBottomText && renderBottomText()}

                        {maxLength && !hideLimit && renderCharLimit(inputValue)}
                    </FlexDiv>
                </>
            )}
            {variant === 'v2' && (
                <>
                    <Label htmlFor={id}>
                        <Typography
                            mb={'4px'}
                            variant={labelVariant}
                            color={
                                labelColor ||
                                (isLightBg ? 'ambience.23' : 'ambience.1')
                            }
                        >
                            {label}
                        </Typography>
                        {subLabel && (
                            <SubLabel
                                mb={'0px'}
                                variant={'caption'}
                                color={
                                    subLabelColor ||
                                    (isLightBg ? 'ambience.13' : 'ambience.14')
                                }
                            >
                                {subLabel}
                            </SubLabel>
                        )}
                    </Label>
                    <FormFieldBox
                        className={classnames(
                            'form-field',
                            {
                                error: isError,
                                filled:
                                    !props.disabled &&
                                    (isFilled || typeof isFilled === 'number'),
                            },
                            className
                        )}
                        position={custom || hasButtons ? 'relative' : ''}
                        isLightBg={isLightBg}
                        hasError={isError}
                        newOnBoardingFlow={newOnBoardingFlow || false}
                        isForcedLight={isForcedLight}
                    >
                        {!custom && (
                            <StyledInput
                                type={type}
                                name={name}
                                id={id}
                                placeholder={placeholder}
                                maxLength={maxLength}
                                onChange={onValueChange}
                                autoFocus={autoFocus}
                                multiline={multiline}
                                onKeyUp={onKeyUp}
                                onKeyPress={onKeyPress}
                                min={min}
                                max={max}
                                onFocus={onFocus}
                                onBlur={onBlur}
                                {...props}
                            />
                        )}
                        {hasButtons && (
                            <ButtonFlexDiv>
                                <StyledButton
                                    type={'button'}
                                    key={'buttonMinus'}
                                    onClick={onDecrement}
                                    disabled={props.value <= min ? true : false}
                                >
                                    <StyledSvgSprite
                                        icon={'minus-icon'}
                                        fill={'ambience.14'}
                                        height={'16px'}
                                        width={'16px'}
                                    />
                                </StyledButton>
                                <StyledButton
                                    type={'button'}
                                    key={'buttonPlus'}
                                    onClick={onIncrement}
                                    disabled={props.value >= max ? true : false}
                                >
                                    <StyledSvgSprite
                                        icon={'plus-icon'}
                                        fill={'ambience.14'}
                                        height={'16px'}
                                        width={'16px'}
                                    />
                                </StyledButton>
                            </ButtonFlexDiv>
                        )}
                        {custom && <>{children}</>}
                    </FormFieldBox>
                    <FlexDiv
                        className={classnames({
                            'justify-content-end': !renderBottomText,
                            'justify-content-between': renderBottomText,
                        })}
                    >
                        {errorMsg && (
                            <ParagraphV2
                                className={classnames('error-msg', {
                                    invisible: !isError,
                                })}
                                variant={errorTextVarient}
                                color={'semantic.1'}
                            >
                                {errorMsg}
                            </ParagraphV2>
                        )}
                        {renderBottomText && renderBottomText()}

                        {maxLength &&
                            !hideLimit &&
                            name !== 'tableCount' &&
                            renderCharLimit(inputValue)}
                        {!hideLimit &&
                            name === 'tableCount' &&
                            renderSizeLimit()}
                    </FlexDiv>
                </>
            )}
        </>
    );
}
const Label = styled.label``;
const Paragraph = styled.p``;
const ParagraphV2 = styled(Typography)`
    &.error-msg {
        padding-left: 0;
    }
`;
const StyledInput = styled(Input)`
    ${({ multiline }) =>
        multiline &&
        `
        min-height: 37px;
    `};
`;
const FormFieldBox = styled(Box)`
    &.form-field {
        border-radius: 8px;
        padding: 16px;
        border: none;
        input,
        textarea {
            color: ${({ theme, isForcedLight }) =>
                isForcedLight &&
                theme.isLightTheme &&
                theme.colors.ambience[2]};
        }
        &.filled {
            border: none;
            color: ${({ theme, isLightBg }) =>
                isLightBg
                    ? theme.colors.ambience[23]
                    : theme.colors.ambience[2]};
            &.error {
                border: ${({ theme, hasError }) =>
                    hasError
                        ? `1px solid ${theme.colors.semantic[3]}`
                        : 'none'};
            }
            input,
            textarea {
                color: ${({ theme, isForcedLight }) =>
                    isForcedLight &&
                    theme.isLightTheme &&
                    theme.colors.ambience[2]};
            }
        }
        ${StyledInput} {
            ${({ theme, isLightBg }) =>
                isLightBg &&
                `
                color: ${theme.colors.ambience[23]};
            `};
            ${({ theme, newOnBoardingFlow }) =>
                newOnBoardingFlow &&
                `
            color: ${theme.colors.ambience[2]};
        `};
            &:-webkit-autofill {
                -webkit-box-shadow: 0 0 0px 1000px
                    ${({ theme, isLightBg }) =>
                        theme.isLightTheme
                            ? theme.colors.white
                            : isLightBg
                            ? theme.colors.ambience[3]
                            : theme.colors.ambience[20]}
                    inset !important;
                -webkit-text-fill-color: ${({ theme, isLightBg }) =>
                    isLightBg
                        ? theme.colors.ambience[23]
                        : theme.colors.ambience[2]} !important;
                border: none !important;
                caret-color: ${({ theme, isLightBg }) =>
                    theme.isLightTheme
                        ? theme.colors.black
                        : isLightBg
                        ? theme.colors.ambience[23]
                        : theme.colors.white};
                transition: background-color 5000s ease-in-out 5000s;
            }
        }
    }
    background-color: ${({ theme, isLightBg, isForcedLight }) =>
        isLightBg
            ? theme.colors.ambience[3]
            : isForcedLight && theme.isLightTheme
            ? theme.colors.ambience[22]
            : theme.colors.ambience[20]};
    ${({ newOnBoardingFlow }) =>
        newOnBoardingFlow &&
        css`
            background-color: ${({ theme }) =>
                theme.colors.ambience[24]} !important;
            border: ${({ theme }) =>
                `1px solid ${theme.colors.ambience[20]} !important`};
        `}
`;
const SubLabel = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.book};
`;
const StyledButton = styled.button`
    transition: all 0.3s ease-in-out;
    &:disabled {
        opacity: 0.5;
    }
`;
const ButtonFlexDiv = styled(FlexDiv)`
    height: 100%;
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    button {
        margin-right: 8px;
        border: none;
        outline: 0;
        background-color: transparent;
        padding: 0;
        height: 24px;
        width: 24px;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        &:last-child {
            margin-right: 0;
        }
        &:hover,
        &:focus {
            box-shadow: none !important;
            outline: 0;
        }
    }
`;
